// Owner: views-layouts (wave 2, plan §9.23). The configuration of the non-table layouts of a saved
// view — the card designer (board, gallery, list and the phone list), board options (manual order,
// WIP limits, column order, quick add), the calendar and timeline field mappings — plus the pure
// helpers every client shares: badge text templates (`{{days_since(field)}}`), fractional rank keys
// for the manual in-column order (`view_record_rank`), and the explicit defaults that replace the old
// role and slot inference. Import as `@dataroom/core/view-layouts` (re-exported by `/views`).
import { z } from 'zod';
import { FieldPath, type FilterNode } from './filter';
import type { Field, ObjectType } from './metamodel';
import type { ModelIndex } from './model-index';

// ---------------------------------------------------------------------------------------------
// Card designer (board, gallery, list, phone list)
// ---------------------------------------------------------------------------------------------

/** At most this many fields show on a card (lists show 4, the phone list 3). */
export const CARD_FIELDS_MAX = 6;
export const LIST_FIELDS_MAX = 4;
export const MOBILE_FIELDS_MAX = 3;

export const CardSize = z.enum(['s', 'm', 'l']);
export type CardSize = z.infer<typeof CardSize>;
export const CoverFit = z.enum(['cover', 'contain']);
export type CoverFit = z.infer<typeof CoverFit>;
export const CoverAspect = z.enum(['16:9', '4:3', '1:1', '3:4']);
export type CoverAspect = z.infer<typeof CoverAspect>;

/** How one card field shows: with its label, and hidden while it has no value. */
export const CardFieldOptions = z
  .object({ label: z.boolean().default(false), hideEmpty: z.boolean().default(true) })
  .strict();
export type CardFieldOptions = z.infer<typeof CardFieldOptions>;

/**
 * The card designer. Which fields a card shows and in which order stays `ViewConfig.cardFields`
 * (the Fields popover edits it); this adds the cover, the size, the colour and per-field options.
 * `cover.field` is an image or file field; `null` draws a generated cover (the record's initial on
 * the colour of `colorBy`).
 */
export const CardConfig = z
  .object({
    cover: z
      .object({
        field: FieldPath.nullable().default(null),
        fit: CoverFit.default('cover'),
        aspect: CoverAspect.default('16:9'),
      })
      .strict()
      .nullable()
      .default(null),
    size: CardSize.default('m'),
    /** A select, status or member field whose colour tints the card edge (and generated covers). */
    colorBy: FieldPath.nullable().default(null),
    fields: z.record(FieldPath, CardFieldOptions).default({}),
  })
  .strict();
export type CardConfig = z.infer<typeof CardConfig>;
export type CardConfigInput = z.input<typeof CardConfig>;

export const DEFAULT_CARD: CardConfig = { cover: null, size: 'm', colorBy: null, fields: {} };

/** Options of one card field (defaults: no label, hidden when empty — never a "–" placeholder). */
export function cardFieldOptions(card: CardConfig | null | undefined, field: string): CardFieldOptions {
  return card?.fields[field] ?? { label: false, hideEmpty: true };
}

// ---------------------------------------------------------------------------------------------
// Board
// ---------------------------------------------------------------------------------------------

/** WIP limits allow up to this many records per column. */
export const WIP_LIMIT_MAX = 999;

export const BoardConfig = z
  .object({
    /**
     * `manual`: cards keep the order members drag them into (per view, `view_record_rank`); cards
     * nobody ordered yet follow the view's sort. `sorted`: the view's sort decides, in-column drag is
     * off (moving between columns still works).
     */
    order: z.enum(['manual', 'sorted']).default('manual'),
    /** Soft limit per column (group key → count): the header turns amber above it. */
    wipLimits: z.record(z.string().max(200), z.number().int().min(1).max(WIP_LIMIT_MAX)).default({}),
    /** The view's own column order (group keys); columns not listed follow in their natural order. */
    columnOrder: z.array(z.string().max(200)).max(500).default([]),
    /** "+ New" at the top of every column. */
    quickAdd: z.boolean().default(true),
  })
  .strict();
export type BoardConfig = z.infer<typeof BoardConfig>;
export const DEFAULT_BOARD: BoardConfig = { order: 'manual', wipLimits: {}, columnOrder: [], quickAdd: true };

/** Columns in the view's order: listed keys first (in that order), the others after, as they come. */
export function orderColumns<T extends { key: string }>(
  columns: readonly T[],
  order: readonly string[],
): T[] {
  if (order.length === 0) return [...columns];
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const listed = order.map((k) => byKey.get(k)).filter((c): c is T => Boolean(c));
  const seen = new Set(listed.map((c) => c.key));
  return [...listed, ...columns.filter((c) => !seen.has(c.key))];
}

// ---------------------------------------------------------------------------------------------
// Calendar and timeline
// ---------------------------------------------------------------------------------------------

export const CalendarMode = z.enum(['month', 'week', 'day', 'agenda']);
export type CalendarMode = z.infer<typeof CalendarMode>;

/**
 * Calendar field mapping (Layout panel): the start (date or date-time), the end of the same kind or a
 * duration field (else 30 minutes / one day), the title (default: the record title), the colour and a
 * yes/no "all day" field. Date-only starts are all-day events.
 */
export const CalendarConfig = z
  .object({
    start: FieldPath.nullable().default(null),
    end: FieldPath.nullable().default(null),
    duration: FieldPath.nullable().default(null),
    title: FieldPath.nullable().default(null),
    colorBy: FieldPath.nullable().default(null),
    allDay: FieldPath.nullable().default(null),
    mode: CalendarMode.default('week'),
    /** Show Saturday and Sunday in the week view. */
    weekends: z.boolean().default(true),
  })
  .strict();
export type CalendarConfig = z.infer<typeof CalendarConfig>;

export const TimelineZoom = z.enum(['day', 'week', 'month', 'quarter']);
export type TimelineZoom = z.infer<typeof TimelineZoom>;

/**
 * Timeline field mapping: bars from `start` to `end` (a milestone diamond on `milestone`), a progress
 * fill from a percent field, the colour; rows are grouped by the view's `groupBy`.
 */
export const TimelineConfig = z
  .object({
    start: FieldPath.nullable().default(null),
    end: FieldPath.nullable().default(null),
    progress: FieldPath.nullable().default(null),
    milestone: FieldPath.nullable().default(null),
    colorBy: FieldPath.nullable().default(null),
    /** A field shown under each row's title (e.g. the linked record). */
    subtitle: FieldPath.nullable().default(null),
    zoom: TimelineZoom.default('week'),
  })
  .strict();
export type TimelineConfig = z.infer<typeof TimelineConfig>;

const DATE_TYPES = new Set(['date', 'datetime']);
const isDate = (f: Field | undefined): f is Field => Boolean(f && DATE_TYPES.has(f.type));

/**
 * The explicit calendar mapping for an object (what a new calendar view or the migration stores):
 * the `start`/`end` roles when present, else the first date field. Nothing is guessed from field keys.
 */
export function defaultCalendarConfig(model: ModelIndex, object: ObjectType): CalendarConfig {
  const fields = model.fieldsOf(object.id);
  const start =
    fields.find((f) => f.role === 'start' && isDate(f)) ??
    fields.find((f) => (f.role === 'due' || f.role === 'close_date') && isDate(f)) ??
    fields.find(isDate);
  const end = start
    ? fields.find((f) => f.role === 'end' && f.type === start.type && f.id !== start.id)
    : undefined;
  const colorBy =
    fields.find((f) => f.role === 'type' && f.type === 'select') ??
    fields.find((f) => f.type === 'status') ??
    fields.find((f) => f.type === 'select');
  return CalendarConfig.parse({
    start: start?.key ?? null,
    end: end?.key ?? null,
    colorBy: colorBy?.key ?? null,
    // Day-granular events (no time of day) read best as a month; a time grid would be empty.
    mode: start?.type === 'date' ? 'month' : 'week',
  });
}

/** The explicit timeline mapping for an object (start/end roles, a percent progress field). */
export function defaultTimelineConfig(model: ModelIndex, object: ObjectType): TimelineConfig {
  const fields = model.fieldsOf(object.id);
  const start = fields.find((f) => f.role === 'start' && isDate(f)) ?? fields.find(isDate);
  const end =
    fields.find((f) => f.role === 'end' && isDate(f) && f.id !== start?.id) ??
    fields.find((f) => (f.role === 'due' || f.role === 'close_date') && isDate(f) && f.id !== start?.id) ??
    fields.find((f) => isDate(f) && f.id !== start?.id);
  const progress = fields.find((f) => f.role === 'progress' && f.type === 'percent');
  const subtitle = fields.find((f) => f.type === 'relation' && model.relationTarget(f)?.single);
  return TimelineConfig.parse({
    start: start?.key ?? null,
    end: end?.key ?? null,
    progress: progress?.key ?? null,
    subtitle: subtitle?.key ?? null,
  });
}

// ---------------------------------------------------------------------------------------------
// Card slot inference → explicit card config (migration and the blueprint views adapter)
// ---------------------------------------------------------------------------------------------

/**
 * The card config that reproduces what the old board card showed for `cardFields`: the first single
 * relation, currency, date and member fields were drawn without a label ("slots"), every other field
 * with its label. Empty values are hidden instead of "–" / "No …" placeholders.
 */
export function legacyCardConfig(
  model: ModelIndex,
  object: ObjectType,
  cardFields: ReadonlyArray<{ field: string; visible: boolean }>,
): CardConfig {
  const fields = cardFields
    .filter((c) => c.visible)
    .map((c) => model.fieldByKey(object.id, c.field))
    .filter((f): f is Field => Boolean(f));
  const slot = new Set<string>();
  const first = (pick: (f: Field) => boolean) => {
    const f = fields.find((x) => pick(x));
    if (f) slot.add(f.key);
  };
  first((f) => f.type === 'relation' && Boolean(model.relationTarget(f)?.single));
  first((f) => f.type === 'currency');
  first((f) => f.type === 'date' || f.type === 'datetime');
  first((f) => f.type === 'user');
  const options: CardConfig['fields'] = {};
  for (const f of fields) options[f.key] = { label: !slot.has(f.key), hideEmpty: true };
  return { ...DEFAULT_CARD, fields: options };
}

// ---------------------------------------------------------------------------------------------
// Badge text templates
// ---------------------------------------------------------------------------------------------

const TEMPLATE = /\{\{\s*(days_since|days_until)\(\s*([a-z][a-z0-9_]*)\s*\)\s*\}\}/g;
const DAY_MS = 86_400_000;

/**
 * Fill `{{days_since(field)}}` / `{{days_until(field)}}` with whole days between the record's value
 * and `nowMs` ("Stalled {{days_since(stage_changed_at)}}d" → "Stalled 26d"). A missing value renders
 * as an empty string; numbers use `formatNumber` (the member's digits).
 */
export function renderBadgeText(
  text: string,
  values: Readonly<Record<string, unknown>>,
  nowMs: number,
  formatNumber: (n: number) => string = String,
): string {
  return text
    .replace(TEMPLATE, (_, fn: string, key: string) => {
      const raw = values[key];
      if (typeof raw !== 'string' || !raw) return '';
      const at = Date.parse(raw.length === 10 ? `${raw}T00:00:00Z` : raw);
      if (Number.isNaN(at)) return '';
      const days = Math.floor((fn === 'days_since' ? nowMs - at : at - nowMs) / DAY_MS);
      return formatNumber(Math.max(0, days));
    })
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// ---------------------------------------------------------------------------------------------
// Badge presets (the former board "signals", as editable conditional rules)
// ---------------------------------------------------------------------------------------------

/** A badge rule as stored in `ViewConfig.rules` (`display: 'badge'`). */
export type BadgeRule = {
  id: string;
  label: { en: string; de: string };
  filter: FilterNode;
  display: 'badge';
  tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent';
  text: { en: string; de: string };
};

/**
 * Badge rules an object's metadata supports, replacing the closed signal enum:
 * - **stalled** — records longer in an open stage than its "stale after" days ("Stalled 26d"; one
 *   rule, an alternative per distinct limit), needs a status field with stale limits and a
 *   `stage_changed_at` field;
 * - **today** — a linked record of an object with the `activity` trait starts today, through a
 *   relation field that points at it.
 * Ids are `preset-<kind>[-<field>]`, so a second run finds what it added. Approval and document-view
 * chips have no filter equivalent and are not presets (the data migration reports them).
 */
export function badgePresets(model: ModelIndex, object: ObjectType): BadgeRule[] {
  const out: BadgeRule[] = [];
  const fields = model.fieldsOf(object.id);
  const status = fields.find((f) => f.role === 'stage') ?? fields.find((f) => f.type === 'status');
  const changed = model.fieldByRole(object.id, 'stage_changed_at');
  if (status && changed) {
    const byDays = new Map<number, string[]>();
    for (const s of model.stagesForField(status.id))
      if (s.group === 'open' && s.staleAfterDays)
        byDays.set(s.staleAfterDays, [...(byDays.get(s.staleAfterDays) ?? []), s.key]);
    // One badge for every open stage with a limit: stages that share a limit share a condition, and
    // the conditions of different limits are alternatives.
    const groups: FilterNode[] = [...byDays]
      .sort((a, b) => a[0] - b[0])
      .map(([days, keys]) => ({
        and: [
          { field: status.key, op: 'in', value: [...new Set(keys)] },
          { field: changed.key, op: 'before', value: { $rel: `-${days}d` } },
        ],
      }));
    if (groups.length) {
      const since = `{{days_since(${changed.key})}}`;
      out.push({
        id: 'preset-stalled',
        label: { en: 'Stalled', de: 'Stockt' },
        filter: groups.length === 1 ? groups[0]! : { or: groups },
        display: 'badge',
        tone: 'warning',
        text: { en: `Stalled ${since}d`, de: `Stockt seit ${since} T.` },
      });
    }
  }
  for (const f of fields) {
    const target = model.relationTarget(f);
    if (!target || !target.target.traits.includes('activity')) continue;
    const start = model.fieldByRole(target.target.id, 'start');
    if (!start || !isDate(start)) continue;
    out.push({
      id: `preset-today-${f.key}`.slice(0, 40),
      label: { en: 'Scheduled today', de: 'Heute geplant' },
      filter: { field: `${f.key}.${start.key}`, op: 'eq', value: { $rel: 'today' } },
      display: 'badge',
      tone: 'accent',
      text: { en: 'Scheduled today', de: 'Heute geplant' },
    });
    break;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Fractional rank keys (manual in-column order)
// ---------------------------------------------------------------------------------------------

/** Base-62 digits in byte order, so keys sort the same in JavaScript and in Postgres `collate "C"`. */
const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
export const RANK_KEY_PATTERN = /^[0-9A-Za-z]*[1-9A-Za-z]$/;

function midpoint(a: string, b: string | null): string {
  if (b !== null) {
    let n = 0;
    while ((a[n] ?? '0') === b[n]) n++;
    if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
  }
  const digitA = a ? DIGITS.indexOf(a[0]!) : 0;
  const digitB = b !== null ? DIGITS.indexOf(b[0]!) : DIGITS.length;
  if (digitB - digitA > 1) return DIGITS[Math.round((digitA + digitB) / 2)]!;
  if (b !== null && b.length > 1) return b.slice(0, 1);
  return DIGITS[digitA]! + midpoint(a.slice(1), null);
}

/** A key strictly between `a` and `b` (`null` = open end). */
export function rankBetween(a: string | null, b: string | null): string {
  if (a !== null && !RANK_KEY_PATTERN.test(a)) throw new Error(`Invalid rank key "${a}"`);
  if (b !== null && !RANK_KEY_PATTERN.test(b)) throw new Error(`Invalid rank key "${b}"`);
  if (a !== null && b !== null && a >= b) throw new Error(`Rank keys out of order: "${a}" ≥ "${b}"`);
  return midpoint(a ?? '', b);
}

/**
 * `POST /objects/:key/views/:view/ranks` — a card was dropped: `order` is the target column's loaded
 * records (ids, top to bottom) with `recordId` at its new place. The server ranks the record between
 * its neighbours (ranking the column's loaded records first when they have no rank yet).
 */
export const RankMoveInput = z
  .object({
    recordId: z.uuid(),
    order: z.array(z.uuid()).min(1).max(200),
  })
  .strict()
  .refine((v) => v.order.includes(v.recordId), { message: 'order must contain recordId', path: ['order'] })
  .refine((v) => new Set(v.order).size === v.order.length, {
    message: 'order has duplicates',
    path: ['order'],
  });
export type RankMoveInput = z.infer<typeof RankMoveInput>;

/** The rank keys written by a move (record id → key). */
export const RankMoveResult = z.object({ ranks: z.record(z.string(), z.string()) });
export type RankMoveResult = z.infer<typeof RankMoveResult>;

/** `n` increasing keys between `a` and `b`, as short as possible (bisection). */
export function ranksBetween(a: string | null, b: string | null, n: number): string[] {
  if (n <= 0) return [];
  if (n === 1) return [rankBetween(a, b)];
  const mid = Math.floor(n / 2);
  const c = rankBetween(a, b);
  return [...ranksBetween(a, c, mid), c, ...ranksBetween(c, b, n - mid - 1)];
}
